import type { ReactNode, Ref } from 'react';
import { merge } from 'lodash-es';

import type { IconName } from '../../assets/Icon/Icon';
import type { VariantProp } from '../../common/colors';
import type { GutterProp } from '../../utilities/shared/Gutter';
import type { SpacingScale } from '../../utilities/shared/sizes';
import { Icon } from '../../assets/Icon/Icon';
import { sizing } from '../../common/sizing';
import {
  getVariantStyles,
  variantBackgroundColor,
  variantBodyColor,
  variantHeadingColor,
  variantIconColor,
  variantStrokeColor,
} from '../../common/variant';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import { styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { SmallSansSizes } from '../../text/Small';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { gutterCSS } from '../../utilities/shared/Gutter';
import { spacingCSS } from '../../utilities/shared/Spacing';

const alertContainerWidth = 720;

const alertContainersSizes = {
  narrow: alertContainerWidth,
  intermediate: alertContainerWidth * 2,
  wide: alertContainerWidth * 3,
};

const alertContainers = {
  all: `@container alert (min-width: 0)`,
  narrow: `@container alert (max-width: ${alertContainersSizes.narrow}px)`,
  notNarrow: `@container alert (min-width: ${alertContainersSizes.narrow + 1}px)`,
  intermediate: `@container alert (min-width: ${alertContainersSizes.narrow + 1}px) and (max-width: ${alertContainersSizes.intermediate}px)`,
  notWide: `@container alert (max-width: ${alertContainersSizes.intermediate}px)`,
  wide: `@container alert (min-width: ${alertContainersSizes.intermediate + 1}px)`,
};

const AlertActionsContainer = styled(ControlGroup, {
  display: 'none',

  variants: {
    position: {
      shoulder: {
        [alertContainers.notNarrow]: {
          display: 'flex',
        },
      },
      trailing: {
        [alertContainers.narrow]: {
          display: 'flex',
          paddingTop: '$8',
        },
      },
    },
  },
});
AlertActionsContainer.displayName = 'AlertActionsContainer';

function AlertActions({
  children,
  position,
  type,
}: {
  children: ReactNode;
  position: 'shoulder' | 'trailing';
  type: AlertType;
}) {
  return (
    <AlertActionsContainer
      position={position}
      relation="separate"
      size={type === 'inline' ? 'x-small' : 'small'}
    >
      {children}
    </AlertActionsContainer>
  );
}

const AlertIcon = styled(Icon, {
  color: variantIconColor,

  variants: {
    type: {
      full: {
        width: '$16',
        height: '$16',
      },
      inline: {
        width: '$12',
        height: '$12',
      },
    },
  },
});
AlertIcon.displayName = 'AlertIcon';

const AlertHeading = styled(Text, {
  color: variantHeadingColor,
  fontWeight: '$bold',
  wordBreak: 'break-word',

  variants: {
    type: {
      full: {
        ...BodySansSizes,
      },
      inline: {
        ...SmallSansSizes,
      },
    },
  },
});
AlertHeading.displayName = 'AlertHeading';

const AlertCopy = styled(Text, {
  color: variantBodyColor,
  wordBreak: 'break-word',

  variants: {
    type: {
      full: {
        ...BodySansSizes,
      },
      inline: {
        ...SmallSansSizes,
      },
    },
  },
});
AlertCopy.displayName = 'AlertCopy';

const AlertContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  width: '100%',
});
AlertContent.displayName = 'AlertContent';

export const AlertContainer = styled('div', {
  position: 'relative',
  container: 'alert / inline-size',
  display: 'flex',
  alignItems: 'flex-start',
  width: '100%',
  background: variantBackgroundColor,
  strokeAll: variantStrokeColor,

  variants: {
    id: {},
    isDismissed: {
      true: {
        display: 'none',
      },
    },
    relation: {
      standalone: {},
      stacked: {},
    },
    type: {
      full: {
        padding: `$10 ${sizing.contentSides}`,
      },
      inline: {
        padding: `$6 ${sizing.contentSides}`,
      },
    },
    variant: {
      alternative: {
        zIndex: 3,
        ...getVariantStyles('alternative'),
      },
      brand: {
        zIndex: 3,
        ...getVariantStyles('brand'),
      },
      negative: {
        zIndex: 3,
        ...getVariantStyles('negative'),
      },
      attention: {
        zIndex: 3,
        ...getVariantStyles('attention'),
      },
      neutral: {
        zIndex: 1,
        ...getVariantStyles('neutral'),
      },
      positive: {
        zIndex: 3,
        ...getVariantStyles('positive'),
      },
    },
    internal: {
      true: {},
      false: {},
    },
  },

  compoundVariants: [
    {
      relation: 'standalone',
      type: 'full',
      css: {
        borderRadius: '$10',
      },
    },
    {
      relation: 'standalone',
      type: 'inline',
      css: {
        borderRadius: '$10',
      },
    },
    {
      relation: 'stacked',
      type: 'inline',
      css: {
        padding: `$6 ${sizing.contentSides}`,
      },
    },
    {
      relation: 'stacked',
      type: 'full',
      css: {
        padding: `$10 ${sizing.contentSides}`,
      },
    },
    {
      relation: 'standalone',
      type: 'inline',
      css: {
        padding: `$6 $10`,
      },
    },
    {
      relation: 'standalone',
      type: 'full',
      css: {
        padding: `$10 $16`,
      },
    },
    {
      internal: true,
      css: {
        zIndex: 3,
        ...getVariantStyles('neutral', true),
      },
    },
  ],
});
AlertContainer.displayName = 'AlertContainer';

export type AlertRelation = 'stacked' | 'standalone';
export type AlertType = 'full' | 'inline';

export type AlertProps = {
  /**
   * The more detailed information regarding the notice, no value disables no copy.
   */
  copy?: ReactNode;
  /**
   * Provide an object to customize the dismiss action of the alert.
   */
  dismiss?: {
    /**
     * Provide an accessible label for the dismiss action of the alert.
     */
    label?: string;
    /**
     * Pass in the onClick event for dismissing the alert.
     */
    onClick?: (event: any) => void;
    /**
     * Set whether the alert is suppressed (i.e. hidden from view).
     */
    suppressed?: boolean;
  };
  /**
   * A simple heading for the notice, no value disables no heading.
   */
  heading?: ReactNode;
  /**
   * Set which icon to display, no value displays no icon.
   */
  icon?: IconName;
  /**
   * Pass in an ID to make sure the Alert is identifiable.
   */
  id?: ReactNode;
  /**
   * Set whether the alert is internal.
   */
  internal?: boolean;
  /**
   * Set whether the radius should exist or be squared off.
   */
  relation?: AlertRelation;
  /**
   * Actions to be displayed at the top-right of the alert (alongside dismiss when present).
   */
  actions?: ReactNode;
  /**
   * Determine if the component should be the full or inline type.
   */
  type?: AlertType;
  /**
   * Set the most appropriate variant of the component for your use.
   */
  variant?: VariantProp;

  ref?: Ref<HTMLDivElement | null>;
};

export function Alert({
  copy,
  dismiss,
  heading,
  icon,
  id,
  internal,
  relation = 'standalone',
  ref,
  actions,
  type = 'full',
  variant = 'neutral',
  ...remaining
}: AlertProps) {
  const hasEnd = actions || dismiss;
  const dismissLabel = dismiss?.label ?? 'Dismiss';

  return (
    <AlertContainer
      isDismissed={dismiss?.suppressed}
      variant={variant}
      relation={relation}
      type={type}
      internal={internal}
      ref={ref}
      {...remaining}
    >
      <AlignStack
        direction="row"
        align="start"
        gap={type === 'full' ? 8 : 6}
        preset={type === 'full' ? 'body' : 'small'}
        start={icon && <AlertIcon icon={icon} type={type} />}
        end={
          hasEnd && (
            <ControlGroup relation="separate" size="small">
              {actions && (
                <AlertActions position="shoulder" type={type}>
                  {actions}
                </AlertActions>
              )}
              {dismiss && dismiss.onClick && (
                <UtilityControl
                  onClick={dismiss.onClick}
                  icon="cross"
                  label={dismissLabel}
                  variant={variant}
                />
              )}
            </ControlGroup>
          )
        }
      >
        {(heading || copy) && (
          <AlertContent>
            {heading && <AlertHeading type={type}>{heading}</AlertHeading>}
            {copy && <AlertCopy type={type}>{copy}</AlertCopy>}
            {actions && (
              <AlertActions position="trailing" type={type}>
                {actions}
              </AlertActions>
            )}
          </AlertContent>
        )}
      </AlignStack>
    </AlertContainer>
  );
}

const AlertsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',

  variants: {
    relation: {
      stacked: {},
      standalone: {},
    },
    type: {
      full: {},
      inline: {},
    },
  },
});
AlertsContainer.displayName = 'AlertsContainer';

export type AlertsProp = (Omit<AlertProps, 'id' | 'relation' | 'type'> & { id: string })[];

export type AlertsProps = {
  /**
   * The alerts to display using the alert properties, the id is required.
   */
  alerts: AlertsProp;
  /**
   * The gap between the alerts (only applies to standalone relation).
   */
  gap?: SpacingScale;
  /**
   * The relation of the alerts.
   */
  relation?: AlertRelation;
  /**
   * The type of the alerts.
   */
  type?: AlertType;
} & GutterProp;

export function Alerts({
  alerts,
  gap = 8,
  gutter = { context: 'application', edge: 'none', placement: 'outside' },
  relation = 'stacked',
  type = 'full',
}: AlertsProps) {
  const gutterStyles = gutterCSS(gutter);
  const css = relation === 'standalone' ? merge(spacingCSS(gap), gutterStyles) : gutterStyles;
  return (
    <AlertsContainer relation={relation} type={type} css={css}>
      {alerts.map((alert) => (
        <Alert key={alert.id} {...alert} relation={relation} type={type} />
      ))}
    </AlertsContainer>
  );
}
